import {
  AdmPlan,
  SubjectType,
  SubjectTypeProps,
} from "@custom-types/base-types";
import { DateTimeUtils } from "@stellar/utils";
import { PlanCreationState } from "@store/plan-creation/plan-creation-slice";
import { formatPlanName } from "@utils/data-display";
import {
  CREDITS_DISPLAY_NAME,
  formatLimitType,
} from "@utils/limit-data-display";
import { PlanDuration, validatePlanDuration } from "@utils/plan-duration";
import { BaseProduct } from "@utils/product/product-utils-types";

interface ValidatePlanCreationProps extends SubjectTypeProps, PlanDuration {
  /** Type of the plan */
  subjectType: SubjectType;

  /** The selected feature or bundle to add to plan */
  selectedProduct: BaseProduct | null;

  /** Comments provided by the user */
  comments: string;

  /** A boolean flag that indicates whether the plan should have limits or not */
  shouldShowLimits: boolean;

  /** List of active limits */
  allLimits: PlanCreationState["allLimits"];

  /** Start date of the plan */
  startDate: number;

  /** End date of the plan */
  endDate: number;

  /** How many tokens every year of the plan grants */
  tokensPerYear: number;
}

/** This function validates the plan creation data and returns an error message or null if valid */
export function validatePlanCreation({
  subjectType,
  selectedProduct,
  comments,
  shouldShowLimits,
  allLimits,
  startDate,
  endDate,
  durationType,
  durationValue,
  tokensPerYear,
}: ValidatePlanCreationProps): string | null {
  // A feature must be selected
  if (selectedProduct === null) {
    return subjectType === "DEVICE"
      ? "Please select a license type"
      : "Please select a product";
  }
  // Adding a comment for all type of plans is mandatory
  if (!comments) {
    return "Please add a comment";
  }

  // Checked before the dates, which are derived from the duration and would report a less helpful error
  const durationError = validatePlanDuration({ durationType, durationValue });
  if (durationError) {
    return durationError;
  }

  if (!DateTimeUtils.isValidDate(startDate)) {
    return "Please select a valid start date";
  }

  if (!DateTimeUtils.isValidDate(endDate)) {
    return "Please select a valid end date";
  }

  if (
    startDate &&
    endDate &&
    DateTimeUtils.diffBetweenTwoDatesInDays(startDate, endDate) > 0
  ) {
    return "Please select an end date that comes after the start date";
  }

  if (shouldShowLimits && allLimits.every((limit) => !limit.isEnabled)) {
    return "Please add at least one valid limit";
  }

  for (const limit of allLimits) {
    if (limit.isEnabled && limit.value <= 0) {
      return `Please enter a valid ${formatLimitType(limit.type)} limit`;
    }
  }

  // A plan may well include no tokens at all, so 0 is accepted here. A value that is not a
  // finite number has to be rejected explicitly: it would pass a plain `< 0` check and then be
  // dropped by the `> 0` guard that creates the token plans, leaving the plan without them.
  if (
    selectedProduct.includedTokens &&
    (!Number.isFinite(tokensPerYear) || tokensPerYear < 0)
  ) {
    return `Please enter a valid number of ${CREDITS_DISPLAY_NAME}`;
  }

  return null;
}

/** All the plans that should be shown next to the main plan in plan table */
const ADDITIONAL_PLAN_FEATURES = {
  flash: "Flash",
  ["faro-stream"]: "Stream",
};

/** Returns a name to identify a plan in table. It will add the name of custom features to the end of actual one */
export function formatPlanTableName(plan: AdmPlan): string {
  const additionalFeaturesToDisplay = plan.featureBundles.reduce(
    (acc, bundle) => {
      for (const [key, value] of Object.entries(ADDITIONAL_PLAN_FEATURES)) {
        if (bundle.identifier === key) {
          acc = `${acc} - ${value}`;
        }
      }
      return acc;
    },
    ""
  );

  return `${formatPlanName(plan)}${additionalFeaturesToDisplay}`;
}
